'use client';

import type { VariantProps } from 'class-variance-authority';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getAutoPlacement } from '@/app/components/overlays/tooltip/getAutoPlacement';
import { useClickOutside } from '@/app/hooks/useClickOutside';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

import styles from './style.module.css';

export const DEFAULT_PLACEMENT = 'top-right';

const AUTO_CLOSE_SECOND = 3;

const ARROW_PLACEMENT_CLASSNAME = {
  top: styles.tooltipArrowTop,
  bottom: cn('rotate-180', styles.tooltipArrowBottom),
  center: cn('justify-center'),
};

const TooltipArrowStyled = cnva(
  'absolute left-0 flex h-2 w-full items-center transition-opacity duration-300',
  {
    variants: {
      theme: {
        light: 'text-gray-50',
        dark: 'text-gray-800',
        custom: '',
      },
      arrowPlacement: {
        'top-right': ARROW_PLACEMENT_CLASSNAME.top,
        'top-left': ARROW_PLACEMENT_CLASSNAME.top,
        'bottom-right': ARROW_PLACEMENT_CLASSNAME.bottom,
        'bottom-left': ARROW_PLACEMENT_CLASSNAME.bottom,
        'top-center': [
          ARROW_PLACEMENT_CLASSNAME.top,
          ARROW_PLACEMENT_CLASSNAME.center,
        ],
        'bottom-center': [
          ARROW_PLACEMENT_CLASSNAME.bottom,
          ARROW_PLACEMENT_CLASSNAME.center,
        ],
      },
      arrowCenter: {
        true: 'justify-center',
        false: '',
      },
      isVisible: {
        true: 'opacity-100',
        false: 'opacity-0',
      },
    },
    compoundVariants: [
      {
        arrowCenter: false,
        arrowPlacement: ['top-right', 'bottom-left'],
        className: 'justify-end pr-2.5',
      },
      {
        arrowCenter: false,
        arrowPlacement: ['top-left', 'bottom-right'],
        className: 'pl-2.5',
      },
    ],
    defaultVariants: {
      isVisible: false,
      arrowCenter: true, // Default is to center the arrow position from the trigger element
      arrowPlacement: DEFAULT_PLACEMENT,
      theme: 'light',
    },
  }
);

const TOOLTIP_PLACEMENT_CLASSNAME = {
  top: styles.tooltipBoxTop,
  bottom: styles.tooltipBoxBottom,
  center: cn('center-x'),
  left: cn('left-0'),
  right: cn('right-0'),
};

const TooltipStyled = cnva('absolute', {
  variants: {
    theme: {
      light: 'bg-gray-50',
      dark: 'bg-gray-800 text-white',
      custom: '',
    },
    placement: {
      'top-center': [
        TOOLTIP_PLACEMENT_CLASSNAME.top,
        TOOLTIP_PLACEMENT_CLASSNAME.center,
      ],
      'top-left': [
        TOOLTIP_PLACEMENT_CLASSNAME.top,
        TOOLTIP_PLACEMENT_CLASSNAME.left,
      ],
      'top-right': [
        TOOLTIP_PLACEMENT_CLASSNAME.top,
        TOOLTIP_PLACEMENT_CLASSNAME.right,
      ],
      'bottom-center': [
        TOOLTIP_PLACEMENT_CLASSNAME.bottom,
        TOOLTIP_PLACEMENT_CLASSNAME.center,
      ],
      'bottom-right': [
        TOOLTIP_PLACEMENT_CLASSNAME.bottom,
        TOOLTIP_PLACEMENT_CLASSNAME.right,
      ],
      'bottom-left': [
        TOOLTIP_PLACEMENT_CLASSNAME.bottom,
        TOOLTIP_PLACEMENT_CLASSNAME.left,
      ],
    },
  },
  compoundVariants: [
    {
      theme: ['light', 'dark'],
      className: 'rounded-2xs text-body-1-fixed w-71 px-4 py-3',
    },
  ],
  defaultVariants: {
    placement: DEFAULT_PLACEMENT,
    theme: 'light',
  },
});

type TooltipStylesProps = VariantProps<typeof TooltipStyled>;
type TooltipPlacementTheme = TooltipStylesProps['theme'];
export type TooltipPlacementType = TooltipStylesProps['placement'];

export interface TooltipProps {
  text?: string;
  element?: ReactElement | null;
  placement?: TooltipPlacementType;
  theme?: TooltipPlacementTheme;
  show?: boolean; // Controlled visibility (when manualShow is true)
  manualShow?: boolean; // If true, `show` prop controls visibility
  autoPlacement?: boolean; // If true, tooltip attempts to auto-position
  autoClose?: boolean; // If true, tooltip auto-closes after a duration or on outside click
  disableClickOutside?: boolean; // If true, tooltip auto-closes after a duration or on outside click
  showDelay?: number; // Delay in ms before showing
  children: ReactNode; // Trigger element (required)
  wrapperClassName?: string;
  tooltipClassName?: string;
  arrowClassName?: string;
  arrowCenter?: VariantProps<typeof TooltipArrowStyled>['arrowCenter'];
  onClose?: () => void;
}

const ArrowSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="currentColor"
  >
    <path d="M4.28763 6.60627L0 0H14L9.71237 6.60627C8.51267 8.46458 5.49776 8.46458 4.28763 6.60627Z" />
  </svg>
);

export const Tooltip = ({
  text,
  element,
  show = false,
  manualShow = false,
  theme = 'light',
  placement = DEFAULT_PLACEMENT,
  autoPlacement = false,
  autoClose = true,
  disableClickOutside = false,
  arrowCenter = true,
  showDelay = 0, // Default to 0 for no delay,
  children,
  wrapperClassName,
  tooltipClassName,
  arrowClassName,
  onClose,
}: TooltipProps) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const openTimeoutId = useRef<NodeJS.Timeout>(undefined);

  const [calculatedPlacement, setCalculatedPlacement] = useState<
    TooltipPlacementType | undefined
  >(undefined);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const {
    isTransitioning,
    transitionState,
    className: transitionClassName,
  } = useMountTransition({
    ref: tooltipRef,
    isMount: isVisible,
    baseClassName: 'transition-opacity duration-300 ease-bounce opacity-0',
    animateClassName: 'opacity-100',
  });

  const computedPlacement = calculatedPlacement ?? placement;

  const showTooltip = useCallback(() => {
    clearTimeout(openTimeoutId.current);

    if (showDelay > 0) {
      openTimeoutId.current = setTimeout(() => {
        setIsVisible(true);
      }, showDelay);
    } else {
      setIsVisible(true);
    }
  }, [showDelay]);

  const closeTooltip = useCallback(() => {
    setIsVisible(false);
    safeCall(onClose);
    clearTimeout(openTimeoutId.current);
  }, [onClose]);

  const isEnableClickOutside = useMemo(() => {
    if (disableClickOutside) return false;

    if (manualShow && show) return !autoClose;

    return isVisible; // If visible, click outside should work, UNLESS disableClickOutside is true.
  }, [disableClickOutside, manualShow, show, isVisible, autoClose]);

  // Auto-Placement Calculation
  useEffect(() => {
    if (!autoPlacement || !triggerRef.current || !tooltipRef.current) return;

    if (transitionState === 'animate' && isVisible) {
      const result = getAutoPlacement(
        tooltipRef.current,
        triggerRef.current,
        placement
      );
      setCalculatedPlacement(result ?? DEFAULT_PLACEMENT);
    }
  }, [
    transitionState,
    triggerRef,
    tooltipRef,
    isVisible,
    text,
    placement,
    autoPlacement,
  ]);

  useClickOutside(triggerRef, closeTooltip, {
    enable: isEnableClickOutside, // This now just controls the click outside itself
  });

  // Auto-Close Logic
  useEffect(() => {
    if (!isVisible || !autoClose) {
      return;
    }

    // Store the timeout ID so we can clean it up
    const timeoutId = setTimeout(() => {
      setIsVisible(false);
      safeCall(onClose);
    }, AUTO_CLOSE_SECOND * 1000);

    // Cleanup function - this is crucial!
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isVisible, autoClose, onClose]);

  // Manual Show Control
  useEffect(() => {
    if (!manualShow) return;

    if (show) {
      showTooltip();
    } else {
      closeTooltip();
    }
  }, [manualShow, show, showTooltip, closeTooltip]);

  return (
    <div
      ref={triggerRef}
      className={cn(
        'group relative inline-flex outline-none',
        wrapperClassName
      )}
      {...createDataAttributes({
        test: 'tooltip',
      })}
      onMouseEnter={manualShow ? undefined : showTooltip}
      onClick={manualShow ? undefined : showTooltip}
    >
      {children}
      {!!isTransitioning && (
        <>
          {!!element ? (
            <div
              ref={tooltipRef}
              role="tooltip"
              className={TooltipStyled({
                theme: 'custom',
                placement: computedPlacement,
                className: cn(transitionClassName, tooltipClassName),
              })}
            >
              {element}
            </div>
          ) : (
            !!text && (
              <div
                ref={tooltipRef}
                role="tooltip"
                className={TooltipStyled({
                  theme,
                  placement: computedPlacement,
                  className: cn(transitionClassName, tooltipClassName),
                })}
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
              />
            )
          )}
          <div
            className={TooltipArrowStyled({
              theme: !!element ? 'custom' : theme,
              isVisible,
              arrowCenter,
              arrowPlacement: computedPlacement,
              className: cn(transitionClassName, arrowClassName),
            })}
          >
            <ArrowSvg />
          </div>
        </>
      )}
    </div>
  );
};
